import { useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Building2, LayoutDashboard, Menu, Moon, Sun, UserRound, X } from 'lucide-react';
import { useApp } from '@/app/AppProvider';
import { Brand } from '@/components/layout/Brand';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const links = [
  { to: '/simulacro', label: 'Simulacro' },
  { to: '/estudio', label: 'Estudiar' },
  { to: '/precios', label: 'Precios' },
  { to: '/flotas', label: 'Para flotas' },
];

function NavigationLinks({ mobile = false }: { mobile?: boolean }) {
  return (
    <nav className={cn(mobile ? 'grid gap-1' : 'flex items-center gap-1')} aria-label="Navegación principal">
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          className={({ isActive }) =>
            cn(
              'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              mobile && 'min-h-11 content-center text-base',
            )
          }
        >
          {link.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function SiteHeader({ compact = false }: { compact?: boolean }) {
  const { me, theme, toggleTheme } = useApp();
  const menu = useRef<HTMLDetailsElement>(null);
  const closeMenu = () => {
    if (menu.current) menu.current.open = false;
  };

  return (
    <header className="sticky top-0 z-40 border-b bg-background/92 backdrop-blur supports-[backdrop-filter]:bg-background/82">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
        <Brand compact={compact} />
        {!compact && <div className="hidden md:block"><NavigationLinks /></div>}
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Usar tema claro' : 'Usar tema oscuro'}>
            {theme === 'dark' ? <Sun /> : <Moon />}
          </Button>
          {me?.usuario ? (
            <Button render={<Link to="/panel" />} variant="outline" className="hidden sm:inline-flex">
              <LayoutDashboard /> Mi panel
            </Button>
          ) : (
            <Button render={<Link to="/cuenta/acceso" />} variant="outline" className="hidden sm:inline-flex">
              <UserRound /> Entrar
            </Button>
          )}
          {!compact && (
            <details ref={menu} className="group relative md:hidden">
              <summary className="grid size-11 cursor-pointer list-none place-items-center rounded-lg hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
                <Menu className="group-open:hidden" />
                <X className="hidden group-open:block" />
                <span className="sr-only">Abrir o cerrar menú</span>
              </summary>
              <div className="absolute right-0 top-13 w-[min(22rem,calc(100vw-2rem))] rounded-xl border bg-popover p-4 text-popover-foreground shadow-raised">
                <div onClick={closeMenu}>
                  <NavigationLinks mobile />
                  <Separator className="my-5" />
                  <div className="grid gap-2">
                    {me?.usuario ? (
                      <>
                        <Button render={<Link to="/panel" />}><LayoutDashboard /> Mi panel</Button>
                        {me.flotas.length > 0 && <Button render={<Link to="/flota" />} variant="outline"><Building2 /> Panel de flota</Button>}
                      </>
                    ) : (
                      <>
                        <Button render={<Link to="/cuenta/registro" />}>Crear cuenta gratis</Button>
                        <Button render={<Link to="/cuenta/acceso" />} variant="outline">Ya tengo cuenta</Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
    </header>
  );
}
